import { memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import {
  StepsCarousel,
  type CarouselStep,
} from "../components/StepsCarousel";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { accentInkOnSurface } from "../utils/accentInkOnSurface";
import { getEvents, safeCapture } from "../analytics";
import step3 from "../assets/cash_deposit_how_to/step3.svg?raw";
import step4 from "../assets/cash_deposit_how_to/step4.svg?raw";
import step5 from "../assets/cash_deposit_how_to/step5.svg?raw";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


const ILLUSTRATIONS = [step3, step4, step5];

/** Steps that quote the exact phrase to say at the counter. */
const SCRIPTED_STEPS = new Set(["3"]);

const STEP_KEYS = ["3", "4", "5"] as const;

/**
 * Illustrated how-to carousel for the cash-deposit widget. Re-opened from
 * Cash Stores via "How to deposit?"; map and copy are informational only.
 */
export const HowToPayInCashPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    footerColor,
    title,
  } = colorConfig;
  const partnerColors = session.data?.partner?.theme?.colors as
    | Record<string, string>
    | undefined;
  const accentSolid = accentInkOnSurface(partnerColors, background, title);
  const inactiveColor = partnerColors?.inactived ?? footerColor;

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("how_to_pay_in_cash"));
  }, []);

  const steps = useMemo<CarouselStep[]>(
    () =>
      STEP_KEYS.map((key, position) => ({
        id: key,
        illustration: ILLUSTRATIONS[position],
        title: t(`howToPayInCash.steps.${key}.title`),
        body: t(`howToPayInCash.steps.${key}.body`),
        script: SCRIPTED_STEPS.has(key)
          ? t(`howToPayInCash.steps.${key}.script`)
          : undefined,
      })),
    [t],
  );

  const themeVars = {
    "--cdh-ink": accentSolid,
    "--cdh-mid": `color-mix(in srgb, ${accentSolid} 55%, ${background})`,
    "--cdh-soft": `color-mix(in srgb, ${accentSolid} 22%, ${background})`,
    "--cdh-fill": `color-mix(in srgb, ${accentSolid} 10%, ${background})`,
    "--cdh-neutral": inactiveColor,
    "--cdh-neutral-strong": footerColor,
    "--cdh-white": background,
    "--cdh-title": accentSolid,
    "--cdh-body": bodyCopy,
    "--cdh-muted": footerColor,
    "--cdh-accent": accentSolid,
    "--cdh-surface": background,
    "--cdh-border": inactiveColor,
  } as React.CSSProperties;

  return (
    <AuraLayout colorConfig={colorConfig} className="cdh-page">
      <Layout.Header style={themeVars}>
        <Navbar
          title=""
          color={bodyCopy}
          onBackClick={() => redirectTo(APP_ROUTES.CASH_STORES_PAGE)}
        />
      </Layout.Header>

      <Layout.Content className="cdh-section" style={themeVars}>
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="cdh-hero">
          <PageTitle
            highlight={t("howToPayInCash.titleHighlight")}
            highlightColor={TITLE_COLOR}
            secudnary={t("howToPayInCash.titleSecondary")}
            secudnaryColor={TITLE_COLOR}
          />
        </section>

        <StepsCarousel
          steps={steps}
          regionLabel={t("howToPayInCash.title")}
          stepLabel={(position, total) =>
            t("howToPayInCash.stepOf", { current: position + 1, total })
          }
          goToLabel={(position) =>
            t("howToPayInCash.goToStep", { step: position + 1 })
          }
        />
      </Layout.Content>

      <Layout.Footer style={themeVars}>
        <section className="footer-section cdh-footer">
          <BodyCopy textColor={footerColor} className="cdh-store-count">
            {t("howToPayInCash.storeCount")}
          </BodyCopy>
          <Button
            type="button"
            size="large"
            variant="outline"
            className="cdh-secondary-cta"
            background={backgroundBtn}
            color={accentSolid}
            style={{ backgroundColor: background }}
            onClick={() =>
              redirectTo(`${APP_ROUTES.STORES_MAP_PAGE}?from=cash-deposit`)
            }
          >
            {t("howToPayInCash.findStores")}
          </Button>
          <Button
            type="button"
            size="large"
            className="lola-return-chat"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => redirectTo(APP_ROUTES.CASH_STORES_PAGE)}
          >
            {t("howToPayInCash.done")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
